import { useState } from 'react';

export default function ExpertHelpSection() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    phone: '',
    employees: '',
    currentSETA: '',
    challenges: '',
    services: [] as string[]
  });

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleServiceChange = (service: string) => {
    setFormData(prev => ({
      ...prev,
      services: prev.services.includes(service)
        ? prev.services.filter(s => s !== service)
        : [...prev.services, service]
    }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    // Handle form submission
    console.log('Form submitted:', formData);
  };

  return (
    <section className="section-wrap bg-gradient-to-br from-[#2D7F3E] to-green-600">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <h2 className="section-heading text-white">
            Get Expert Help
          </h2>
          <p className="text-xl text-green-100 max-w-3xl mx-auto">
            Schedule your complimentary SETA compliance assessment and discover how much you could be saving
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
          {/* Services Overview */}
          <div className="bg-white/10 backdrop-blur-sm p-8 rounded-xl">
            <h3 className="text-2xl font-bold text-white mb-6">Our SETA Compliance Services</h3>
            
            <div className="space-y-6">
              <div className="flex items-start">
                <div className="w-12 h-12 bg-white/20 rounded-lg flex items-center justify-center mr-4 mt-1">
                  <i className="ri-file-edit-line text-white text-xl"></i>
                </div>
                <div>
                  <h4 className="text-lg font-semibold text-white mb-2">Complete WSP & ATR Management</h4>
                  <p className="text-green-100">End-to-end preparation, submission, and follow-up for all mandatory compliance requirements.</p>
                </div>
              </div>

              <div className="flex items-start">
                <div className="w-12 h-12 bg-white/20 rounded-lg flex items-center justify-center mr-4 mt-1">
                  <i className="ri-money-dollar-circle-line text-white text-xl"></i>
                </div>
                <div>
                  <h4 className="text-lg font-semibold text-white mb-2">Grant Application Support</h4>
                  <p className="text-green-100">Professional assistance with discretionary grant applications to maximise funding recovery.</p>
                </div>
              </div>

              <div className="flex items-start">
                <div className="w-12 h-12 bg-white/20 rounded-lg flex items-center justify-center mr-4 mt-1">
                  <i className="ri-user-settings-line text-white text-xl"></i>
                </div>
                <div>
                  <h4 className="text-lg font-semibold text-white mb-2">SDF Training & Support</h4>
                  <p className="text-green-100">Comprehensive training for your Skills Development Facilitator or external SDF services.</p>
                </div>
              </div>

              <div className="flex items-start">
                <div className="w-12 h-12 bg-white/20 rounded-lg flex items-center justify-center mr-4 mt-1">
                  <i className="ri-line-chart-line text-white text-xl"></i>
                </div>
                <div>
                  <h4 className="text-lg font-semibold text-white mb-2">B-BBEE Optimization</h4>
                  <p className="text-green-100">Advisory on the skills development element of your B-BBEE scorecard, to help you maximise your points.</p>
                </div>
              </div>
            </div>

            <div className="mt-8 p-6 bg-white/20 rounded-lg">
              <h4 className="text-xl font-bold text-white mb-3">Why Choose Sylman Academy?</h4>
              <ul className="space-y-2 text-green-100">
                <li className="flex items-center">
                  <i className="ri-check-line text-white mr-2"></i>
                  <span>15+ years of SETA compliance expertise</span>
                </li>
                <li className="flex items-center">
                  <i className="ri-check-line text-white mr-2"></i>
                  <span>98% success rate in grant applications</span>
                </li>
                <li className="flex items-center">
                  <i className="ri-check-line text-white mr-2"></i>
                  <span>QCTO accredited training provider</span>
                </li>
                <li className="flex items-center">
                  <i className="ri-check-line text-white mr-2"></i>
                  <span>Dedicated compliance specialists</span>
                </li>
              </ul>
            </div>
          </div>

          {/* Contact Form */}
          <div className="card p-8">
            <h3 className="text-2xl font-bold text-gray-900 mb-6">
              Schedule Your Complimentary SETA Compliance Assessment
            </h3>
            
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">
                    Full Name *
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    required
                    value={formData.name}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#2D7F3E] focus:border-transparent"
                    placeholder="Your full name"
                  />
                </div>
                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                    Email Address *
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    required
                    value={formData.email}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#2D7F3E] focus:border-transparent"
                    placeholder="your.email@company.com"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label htmlFor="company" className="block text-sm font-medium text-gray-700 mb-2">
                    Company Name *
                  </label>
                  <input
                    type="text"
                    id="company"
                    name="company"
                    required
                    value={formData.company}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#2D7F3E] focus:border-transparent"
                    placeholder="Your company name"
                  />
                </div>
                <div>
                  <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-2">
                    Phone Number
                  </label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#2D7F3E] focus:border-transparent"
                    placeholder="+27 10 224 0406"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label htmlFor="employees" className="block text-sm font-medium text-gray-700 mb-2">
                    Number of Employees
                  </label>
                  <select
                    id="employees"
                    name="employees"
                    value={formData.employees}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#2D7F3E] focus:border-transparent"
                  >
                    <option value="">Select range</option>
                    <option value="1-50">1-50 employees</option>
                    <option value="51-150">51-150 employees</option>
                    <option value="151-500">151-500 employees</option>
                    <option value="500+">500+ employees</option>
                  </select>
                </div>
                <div>
                  <label htmlFor="currentSETA" className="block text-sm font-medium text-gray-700 mb-2">
                    Current SETA
                  </label>
                  <select
                    id="currentSETA"
                    name="currentSETA"
                    value={formData.currentSETA}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#2D7F3E] focus:border-transparent"
                  >
                    <option value="">Select SETA</option>
                    <option value="W&RSETA">W&RSETA</option>
                    <option value="Services SETA">Services SETA</option>
                    <option value="TETA">TETA</option>
                    <option value="AgriSETA">AgriSETA</option>
                    <option value="MERSETA">MERSETA</option>
                    <option value="BANKSETA">BANKSETA</option>
                    <option value="Other">Other</option>
                    <option value="Not sure">Not sure</option>
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-3">
                  Services Needed (select all that apply)
                </label>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {[
                    'WSP & ATR Preparation',
                    'Grant Applications',
                    'SDF Training',
                    'B-BBEE Optimization',
                    'Compliance Audit',
                    'Training Delivery'
                  ].map((service) => (
                    <label key={service} className="flex items-center">
                      <input
                        type="checkbox"
                        checked={formData.services.includes(service)}
                        onChange={() => handleServiceChange(service)}
                        className="mr-2 w-4 h-4 text-[#2D7F3E] border-gray-300 rounded focus:ring-[#2D7F3E]"
                      />
                      <span className="text-sm text-gray-700">{service}</span>
                    </label>
                  ))}
                </div>
              </div>

              <div>
                <label htmlFor="challenges" className="block text-sm font-medium text-gray-700 mb-2">
                  Current SETA Challenges or Questions
                </label>
                <textarea
                  id="challenges"
                  name="challenges"
                  rows={4}
                  value={formData.challenges}
                  onChange={handleInputChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#2D7F3E] focus:border-transparent"
                  placeholder="Tell us about your current SETA compliance challenges or any specific questions you have..."
                  maxLength={500}
                />
                <p className="text-xs text-gray-500 mt-1">Maximum 500 characters</p>
              </div>

              <button
                type="submit"
                className="w-full bg-[#2D7F3E] text-white py-4 px-6 rounded-lg font-semibold text-lg hover:bg-[#245A2E] transition-colors whitespace-nowrap"
              >
                Schedule Free Assessment
              </button>
            </form>

            <div className="mt-6 text-center">
              <p className="text-sm text-gray-600">
                Or call us directly at <span className="font-semibold text-[#2D7F3E]">+27 10 224 0406</span>
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
